/**
 * Derive capabilities from the producer_capabilities_v view row.
 *
 * This is the ONLY place capability logic lives on the frontend.
 * The view is the source of truth; this function simply maps booleans.
 */
import type {
  ProducerCapabilities,
  ProducerCapabilityKey,
  ProducerMeta,
  PublishChecklist,
} from './types';

interface ViewRow {
  is_verified: boolean | null;
  is_public: boolean | null;
  is_suspended: boolean | null;
  has_cover_image: boolean | null;
  has_name_and_description: boolean | null;
  has_linked_place: boolean | null;
  publish_producer_checklist_ok: boolean | null;
  verification_status: string | null;
  visibility_status: string | null;
  published_at: string | null;
  suspended_at: string | null;
}

function bool(v: boolean | null): boolean {
  return v === true;
}

export function deriveCapabilities(row: ViewRow): ProducerCapabilities {
  const isVerified = bool(row.is_verified);
  const isPublic = bool(row.is_public);
  const isSuspended = bool(row.is_suspended);

  const canOperate = !isSuspended;

  const meta: ProducerMeta = {
    isVerified,
    isPublic,
    isSuspended,
    verificationStatus: (row.verification_status as ProducerMeta['verificationStatus']) ?? 'unverified',
    visibilityStatus: (row.visibility_status as ProducerMeta['visibilityStatus']) ?? 'private',
    publishedAt: row.published_at,
    suspendedAt: row.suspended_at,
  };

  const checklist: PublishChecklist = {
    isVerified,
    hasCoverImage: bool(row.has_cover_image),
    hasNameAndDescription: bool(row.has_name_and_description),
    hasLinkedPlace: bool(row.has_linked_place),
    allMet: bool(row.publish_producer_checklist_ok),
  };

  // Visibility only requires name+description, place (and optionally cover image)
  const visibilityReady =
    checklist.hasNameAndDescription && checklist.hasLinkedPlace;

  const capabilities: Record<ProducerCapabilityKey, boolean> = {
    'manage.shop': canOperate, // Navigation allowed; publish/order guards handle verification
    'manage.events': canOperate, // Navigation allowed; publish guard handles verification
    'manage.images': canOperate,
    'publish.producer': canOperate && visibilityReady && !isPublic,
    'unpublish.producer': canOperate && isPublic,
    'publish.products': canOperate && isVerified && isPublic,
    'publish.events': canOperate && isVerified && isPublic,
  };

  return { meta, checklist, capabilities };
}

/**
 * Simple helper: check if a capability is enabled.
 */
export function can(
  capabilities: Record<ProducerCapabilityKey, boolean> | undefined,
  key: ProducerCapabilityKey
): boolean {
  return capabilities?.[key] === true;
}
